import { UseFormReturnType } from "@mantine/form"

import {
  ChangeDataPointOutput,
  PeriodTypeEnum,
} from "@costory/types/endpoints/billy"
import {
  EventsResponses,
  getEventsFromCategory,
} from "@costory/types/endpoints/events"
import { ExploreResponses } from "@costory/types/endpoints/explorer"
import { Filters } from "@costory/types/filters"

import { DEFAULT_DATE_FORMAT } from "@costory/shared/const"
import dayjs, { Dayjs } from "@costory/shared/dayjs"
import { BUSINESS_METRIC_TECHNICAL_NAME } from "@costory/shared/utils/charts"
import {
  CONSISTENT_COLOR_PALETTE,
  getConsistentChartColor,
} from "@costory/shared/utils/consistentColors"

import { EVENTS_NAME } from "@costory/front/components/charts/ChartTooltip"

// Use consistent color system
export const COLOR_PALETTE = CONSISTENT_COLOR_PALETTE

export const getCharteSerieColor = (index: number) => {
  return getConsistentChartColor(index)
}

export interface PayloadItem {
  className?: string
  style?: React.CSSProperties
  name: string
  fill?: string
  stroke?: string
  fillOpacity?: number
  strokeOpacity?: number
  dataKey: string
  color: string
  value: [number, number]
  payload: {
    cost: [number, number]
    name: string
    color: string
    fill?: string
  }
  hide?: boolean
}
export const truncateLabel = (label: string, maxLength: number) => {
  return label.length > maxLength ? `${label.slice(0, maxLength)}...` : label
}

export function extractEvents(
  events: EventsResponses.Aggregated,
  el: {
    [key: string]: string | number | boolean | null | undefined
    agg_date: string
  },
  periodsToDisplay?: { from: Dayjs; to: Dayjs }[],
): number | null {
  const nbrEvents = getEventsFromCategory(events).filter(
    (ev) =>
      el.agg_date === ev.date &&
      (periodsToDisplay
        ? periodsToDisplay.some(
            (period) =>
              dayjs(ev.date) >= period.from && dayjs(ev.date) <= period.to,
          )
        : true),
  )[0]?.events.length
  return nbrEvents > 0 ? nbrEvents : null
}

export function mergeWithEvents(
  data: ExploreResponses.ExplorerData["stackedBarChartData"]["arr"],
  events?: EventsResponses.Aggregated,
  periodsToDisplay?: { from: Dayjs; to: Dayjs }[],
) {
  if (events) {
    return data.map((el) => ({
      ...el,
      [EVENTS_NAME]: extractEvents(events, el, periodsToDisplay),
    }))
  }
  return data
}

export const createHandleLegendClick = (
  toggleMetricLine: () => void,
  toggleComparison: () => void,
  setHiddenSeries: React.Dispatch<React.SetStateAction<Set<string>>>,
) => {
  return (entry: { value: string; dataKey: string }) => {
    if (
      entry.dataKey === BUSINESS_METRIC_TECHNICAL_NAME ||
      entry.dataKey === `${BUSINESS_METRIC_TECHNICAL_NAME} (forecast)`
    ) {
      toggleMetricLine()
    } else if (entry.value.endsWith("(previous)")) {
      toggleComparison()
    } else {
      setHiddenSeries((prev) => {
        const newSet = new Set(prev)
        if (newSet.has(entry.dataKey)) {
          newSet.delete(entry.dataKey)
        } else {
          newSet.add(entry.dataKey)
        }
        return newSet
      })
    }
  }
}

export type CreateIsForecastDateOptions = {
  aggBy?: "Month" | "Week" | "Day"
  now?: string
}

export const createIsForecastDate = (
  includeForecast: boolean,
  historicalEndDate: string,
  options?: CreateIsForecastDateOptions,
) => {
  return (dateStr: string): boolean => {
    if (!includeForecast) return false
    const date = dayjs(dateStr)
    const endDate = dayjs(historicalEndDate).endOf("day")
    const now = options?.now ? dayjs(options.now) : dayjs()
    if (date.isAfter(endDate)) return true
    if (options?.aggBy === "Month" && date.isSame(now, "month")) return true
    if (options?.aggBy === "Week" && date.isSame(now, "week")) return true
    return false
  }
}

export const splitForecastData = (
  data: Record<string, unknown>[],
  includeForecast: boolean,
  isForecastDate: (dateStr: string) => boolean,
  seriesKeys: string[],
  metricKey?: string,
  enableContinuity?: boolean,
): Record<string, unknown>[] => {
  if (!includeForecast) return data

  /**
   * Important contract:
   * - For each original key K in seriesKeys/metricKey we create a forecast key `${K} (forecast)`.
   * - At any given date, only one of K or `${K} (forecast)` should be non-null (except for
   *   continuity helper points at the seam between actual and forecast).
   * - Continuity helper points are marked with a boolean flag stored on the payload:
   *     `_${forecastKey}_artificial`
   *   Tooltip logic in `ChartTooltip` relies on this flag to exclude artificial points from totals.
   */
  const keysToSplit = [...seriesKeys, ...(metricKey ? [metricKey] : [])]
  const lastActualValues: Record<string, number | null> = {}
  const lastActualDates: Record<string, string | null> = {}
  let firstForecastDate: string | undefined

  if (enableContinuity) {
    for (const key of keysToSplit) {
      lastActualValues[key] = null
      lastActualDates[key] = null
    }
    for (let i = data.length - 1; i >= 0; i--) {
      const entry = data[i]
      const dateStr = entry.agg_date as string
      if (isForecastDate(dateStr)) continue
      for (const key of keysToSplit) {
        const v = (entry[key] as number | undefined) ?? null
        if (v != null && lastActualValues[key] === null) {
          lastActualValues[key] = v
          lastActualDates[key] = dateStr
        }
      }
    }
    firstForecastDate = data.find((e) => isForecastDate(e.agg_date as string))
      ?.agg_date as string | undefined
  }

  return data.map((entry) => {
    const dateStr = entry.agg_date as string
    const isForecast = isForecastDate(dateStr)
    const transformedEntry: Record<string, unknown> = { ...entry }

    for (const key of keysToSplit) {
      const value = (entry[key] as number | undefined) ?? null
      const forecastKey = `${key} (forecast)`
      const existingForecast =
        (entry[forecastKey] as number | undefined) ?? null

      if (value != null && existingForecast != null) {
        transformedEntry[key] = value
        const forecastDelta = (existingForecast as number) - (value as number)
        transformedEntry[forecastKey] = Math.max(0, forecastDelta)
        continue
      }

      const lastActual = enableContinuity ? lastActualValues[key] : null
      const lastDate = enableContinuity ? lastActualDates[key] : null

      if (isForecast) {
        const useContinuity =
          enableContinuity &&
          dateStr === firstForecastDate &&
          value === null &&
          existingForecast == null &&
          lastActual != null
        transformedEntry[forecastKey] =
          existingForecast ?? (useContinuity ? lastActual! : value)
        if (useContinuity) transformedEntry[`_${forecastKey}_artificial`] = true
        transformedEntry[key] = null
      } else {
        const transition =
          enableContinuity &&
          lastDate != null &&
          dateStr === lastDate &&
          lastActual != null
        transformedEntry[forecastKey] = transition ? lastActual! : null
        if (transition) transformedEntry[`_${forecastKey}_artificial`] = true
        transformedEntry[key] = value
      }
    }

    return transformedEntry
  })
}

export const getMidDate = (startDate: string, endDate: string) => {
  const startTimestamp = dayjs(startDate).valueOf()
  const endTimestamp = dayjs(endDate).valueOf()
  const midTimestamp = startTimestamp + (endTimestamp - startTimestamp) / 2
  return dayjs(midTimestamp).format(DEFAULT_DATE_FORMAT)
}

export const checkEventsWithinTrends = (
  startDate: string,
  endDate: string,
  events?: EventsResponses.Aggregated,
): boolean => {
  if (!events) return false

  const refStart = dayjs(startDate)
  const refEnd = dayjs(endDate)

  const startRangeStart = refStart.subtract(1, "day")
  const startRangeEnd = refStart.add(1, "day")

  const endRangeStart = refEnd.subtract(1, "day")
  const endRangeEnd = refEnd.add(1, "day")

  // Early return if no events exist
  if (
    !events.businessEvents?.length &&
    !events.providerEvents?.length &&
    !events.technicalEvents?.length
  ) {
    return false
  }

  const allEvents = [
    ...(events.businessEvents || []),
    ...(events.providerEvents || []),
    ...(events.technicalEvents || []),
  ]

  return allEvents.some((group) =>
    group.events.some((event) => {
      const eventDate = dayjs(event.date)
      return (
        eventDate.isSame(startRangeStart, "day") ||
        eventDate.isSame(startRangeEnd, "day") ||
        (eventDate.isAfter(startRangeStart) &&
          eventDate.isBefore(startRangeEnd)) ||
        eventDate.isSame(endRangeStart, "day") ||
        eventDate.isSame(endRangeEnd, "day") ||
        (eventDate.isAfter(endRangeStart) && eventDate.isBefore(endRangeEnd))
      )
    }),
  )
}

export interface ReferenceAreaClickHandler {
  startDate: string
  endDate: string
  handlerEvents?: {
    eventClickOnChart: (
      payload: { agg_date: string },
      dateRange: { from: Dayjs; to: Dayjs },
      fromReferenceArea: boolean,
    ) => void
    createEventFromXAxisClick: (
      payload: { value: string },
      fromReferenceArea: boolean,
    ) => void
  }
  providerEvents?: EventsResponses.Aggregated
}

export const handleReferenceAreaClick = ({
  startDate,
  endDate,
  handlerEvents,
  providerEvents,
}: ReferenceAreaClickHandler): void => {
  if (!handlerEvents) return

  const hasEvents = checkEventsWithinTrends(startDate, endDate, providerEvents)

  if (hasEvents) {
    handlerEvents.eventClickOnChart(
      { agg_date: startDate },
      {
        from: dayjs(startDate),
        to: dayjs(endDate),
      },
      true,
    )
  } else {
    handlerEvents.createEventFromXAxisClick(
      {
        value: getMidDate(startDate, endDate),
      },
      true,
    )
  }
}

export const disableAllEventSources = (form: UseFormReturnType<Filters>) => {
  const disabledEventsFilter = {
    customBusinessEvents: false,
    customTechnicalEvents: false,
    commitments: false,
    marketplacePurchases: false,
    skuPriceChanges: false,
    reservations: false,
    githubSourceRepos: [],
    labels: [],
  }
  form.setFieldValue("eventsFilter", disabledEventsFilter)
}

export const getPeriodsToDisplay = (
  changeDataPointPeriods: ChangeDataPointOutput["periods"],
) => {
  return changeDataPointPeriods
    .filter((period) => period.period_type !== PeriodTypeEnum.NO_TREND)
    .map((period) => [
      {
        from: dayjs(period.period_start).subtract(1, "day"),
        to: dayjs(period.period_start).add(1, "day"),
      },
      {
        from: dayjs(period.period_stop).add(1, "day"),
        to: dayjs(period.period_stop).subtract(1, "day"),
      },
    ])
    .flat()
}
